"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { apiLive } from "@/lib/api";

export type UserOrg = {
  id: string;
  name: string;
  display_name: string;
};

type UserOrganizationsResponse = {
  organizations: UserOrg[];
  count: number;
};

export type UserOrganizationsContextType = {
  orgs: UserOrg[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

const UserOrganizationsContext = createContext<UserOrganizationsContextType | undefined>(
  undefined
);

export const useUserOrganizations = (): UserOrganizationsContextType => {
  const context = useContext(UserOrganizationsContext);
  if (!context) {
    throw new Error(
      "useUserOrganizations must be used within a UserOrganizationsProvider"
    );
  }
  return context;
};

export const UserOrganizationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthContext();

  const [orgs, setOrgs] = useState<UserOrg[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizations = useCallback(async () => {
    if (!isAuthenticated) {
      setOrgs([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiLive<UserOrganizationsResponse>("/api/user/organizations");

      if (response && response.organizations) {
        setOrgs(
          response.organizations.map((org) => ({
            id: org.id,
            name: org.name,
            display_name: org.display_name || org.name,
          }))
        );
      } else {
        setOrgs([]);
        setError("Failed to fetch organizations");
      }
    } catch (err) {
      console.error("Error fetching user organizations:", err);
      setOrgs([]);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch organizations eagerly when user becomes authenticated
  useEffect(() => {
    if (!isAuthLoading) {
      fetchOrganizations();
    }
  }, [isAuthLoading, isAuthenticated]);

  const refetch = useCallback(async () => {
    await fetchOrganizations();
  }, [fetchOrganizations]);

  return (
    <UserOrganizationsContext.Provider
      value={{
        orgs,
        isLoading: isAuthLoading || isLoading,
        error,
        refetch,
      }}
    >
      {children}
    </UserOrganizationsContext.Provider>
  );
};

